import { useEffect, useMemo } from 'react';

import { useCoreDispatch, useCoreSelector } from '../../CoreStore';
import type { AssignedTablesType } from '../../../api/fetchers/assignedTables/types';
import type {
  ICashbackData,
  IPrizeDropData,
  IPromoBet,
  IPromoInformation,
  IPromoPointsData,
  ITournamentData,
} from '../../../api/fetchers/promotions/types';
import { useCurrencyFormatterConnected } from '../../../hooks/useCurrencyFormatterConnected';
import { useBetSlice } from '../bets/selectors';
import { useGameStateSlice } from '../game/selectors';

import { promotionActions } from './slice';
import type { PromotionState, TopTournamentType, TrophyDisplayData } from './types';

export const usePromotionSlice = {
  getPromotions: (): PromotionState => {
    return useCoreSelector(({ promotions }) => promotions);
  },
  getAssignedTables: (): AssignedTablesType | null => {
    return useCoreSelector(({ promotions }) => promotions.assignedTables);
  },
  getPromoPoints: (): IPromoPointsData | null => {
    return useCoreSelector(({ promotions }) => promotions.promoPoints);
  },
  getTopTournament: (): TopTournamentType | undefined => {
    return useCoreSelector(({ promotions }) => promotions.topTournament);
  },
  getWinStreak: (): number | undefined => {
    return useCoreSelector(({ promotions }) => promotions.topTournament?.winStreak);
  },
  getPromotionTranslations: (): Record<string, string> | undefined => {
    return useCoreSelector(({ promotions }) => promotions.translations);
  },
  getPrizeDrops: (): IPrizeDropData[] => {
    return useCoreSelector(({ promotions }) => promotions.prizeDrops);
  },
  getWinPopup: (): IPromoInformation | null => {
    return useCoreSelector(({ promotions }) => promotions.winPopup);
  },
  getTournaments: (): ITournamentData[] => {
    return useCoreSelector(({ promotions }) => promotions.tournaments);
  },
  getCashbacks: (): ICashbackData[] => {
    return useCoreSelector(({ promotions }) => promotions.cashbacks);
  },
  getMinTournamentBet: (): number | null => {
    const allTournaments = useCoreSelector(({ promotions }) => promotions.tournaments);
    const allPrizeDrops = useCoreSelector(({ promotions }) => promotions.prizeDrops);
    const allCashbacks = useCoreSelector(({ promotions }) => promotions.cashbacks);

    const time = Date.now() / 1000;

    const tEnabled = allTournaments.reduce<number | null>((minBet: number | null, tournament: ITournamentData) => {
      if (tournament.opt == 'not_opted' || tournament.sd > time || tournament.ed < time) return minBet;

      const bet =
        tournament.TOURNAMENT.mbl?.find((acc: IPromoBet) => acc.cur == 'EUR')?.bet ||
        tournament.TOURNAMENT.mbl?.[0]?.bet ||
        null;

      return bet && minBet && minBet < bet ? minBet : bet;
    }, null);

    if (tEnabled) return tEnabled;

    const pEnabled = allPrizeDrops.reduce<number | null>((minBet: number | null, prizeDrop: IPrizeDropData) => {
      if (prizeDrop.opt == 'not_opted' || prizeDrop.sd > time || prizeDrop.ed < time) return minBet;

      const bet =
        prizeDrop.PRIZEDROP.mbl?.find((acc: IPromoBet) => acc.cur == 'EUR')?.bet ||
        prizeDrop.PRIZEDROP.mbl?.[0]?.bet ||
        null;

      return bet && minBet && minBet < bet ? minBet : bet;
    }, null);

    if (pEnabled) return pEnabled;

    const cEnabled = allCashbacks.reduce<number | null>((minBet: number | null, cashback: ICashbackData) => {
      if (cashback.opt == 'not_opted' || cashback.sd > time || cashback.ed < time) return minBet;

      const bet =
        cashback.CASHBACK.mbl?.find((acc: IPromoBet) => acc.cur == 'EUR')?.bet ||
        cashback.CASHBACK.mbl?.[0]?.bet ||
        null;

      return bet && minBet && minBet < bet ? minBet : bet;
    }, null);

    return cEnabled;
  },
  getShowTrophyTooltip: (): boolean => {
    return useCoreSelector(({ promotions }) => promotions.showTrophyTooltip);
  },
  getIsRoundLimitExceed: (): boolean => {
    return useCoreSelector(({ promotions }) => promotions.isRoundLimitExceed);
  },
  getWasTrophyTooltipShown: (): boolean => {
    return useCoreSelector(({ promotions }) => promotions.wasTrophyTooltipShown);
  },
};

// TODO move me to slice or to composite hooks
export const useTrophyIcon = (): TrophyDisplayData => {
  const dispatch = useCoreDispatch();
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const totalBet = useBetSlice.totalBet();
  const currencyFormatter = useCurrencyFormatterConnected();

  const showTrophyTooltip = usePromotionSlice.getShowTrophyTooltip();
  const wasTrophyTooltipShown = usePromotionSlice.getWasTrophyTooltipShown();
  const minTournamentBet = usePromotionSlice.getMinTournamentBet();
  const isRoundLimitExceed = usePromotionSlice.getIsRoundLimitExceed();

  useEffect(() => {
    if (!isBetsOpen && minTournamentBet && totalBet && (minTournamentBet > totalBet || isRoundLimitExceed)) {
      if (!wasTrophyTooltipShown) {
        dispatch(promotionActions.changeShowTrophyTooltip(true));
      }
    } else {
      dispatch(promotionActions.changeShowTrophyTooltip(false));
    }
  }, [dispatch, minTournamentBet, totalBet, isBetsOpen, isRoundLimitExceed]);

  return useMemo(() => {
    const trophy: TrophyDisplayData = {
      visibility: 'none',
      minBet: '',
      showTrophyTooltip: showTrophyTooltip,
      isRoundLimitExceed: isRoundLimitExceed,
    };

    if (!isBetsOpen && minTournamentBet && totalBet) {
      if (minTournamentBet <= totalBet && !isRoundLimitExceed) {
        trophy.visibility = 'active';
      } else {
        const formattedMinBet = currencyFormatter(minTournamentBet);
        trophy.minBet = formattedMinBet ? formattedMinBet : '';
        trophy.visibility = 'inactive';
      }
    }
    return trophy;
  }, [showTrophyTooltip, isBetsOpen, minTournamentBet, totalBet, isRoundLimitExceed, currencyFormatter]);
};
